import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, IconButton, InputAdornment, MenuItem, TablePagination, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { StyledWrapper } from "../../pages/dashboard/style";
import {
  StyledBoxStyle,
  StyledCustomerSearch,
  StyledEmbeddedTableChrome,
  StyledEmbeddedTableScroll,
  StyledOrdersContainer,
  StyledStatusFilter,
  StyledTableLayout,
} from "./style";
import {
  StyledSearchIcon,
  StyledTypographyText,
} from "../../pages/virtual-locker/style";
import OrderTable from "../common/table/OrderTable";
import { CUSTOMER_ORDER_LIST } from "../../actions/OrderAction";
import Loader from "../common/loader/Loader";
import {
  ORDER_LIST_COLUMNS,
  ORDER_STATUS_FILTER_OPTIONS,
  normalizeOrderStatus,
  resolveCarrier,
  resolveTrackingNumber,
  resolveTrackingUrl,
} from "../../utils/orderListTable";
import {
  formatOrderDisplayStatusLabel,
  getOrderDisplayStatusBackground,
} from "../../utils/orderDisplayStatus";

const OrderComponent = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const orderListFunc = useCallback(() => {
    dispatch({ type: CUSTOMER_ORDER_LIST, payload: {
      search : debouncedSearch,
      status: statusFilter,
      page,
      limit: rowsPerPage
    }});
  }, [dispatch, debouncedSearch, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    orderListFunc();
  }, [orderListFunc]);

  const {
    recordList: orderList,
    total,
    isCustomerOrderListLoading = false,
    hasFetchedCustomerOrderList = false,
  } = useSelector((state) => state?.order ?? {});

  const showTableLoader =
    !hasFetchedCustomerOrderList ||
    (isCustomerOrderListLoading && !(orderList?.length > 0));

  const columns = ORDER_LIST_COLUMNS;

  const allRecords =
    orderList?.map((item) => {
      const orderStatus = normalizeOrderStatus(item.order_status);
      const isAllApproved = item.order_details?.length > 0 && item.order_details.every(d => d.item.status === "approved");
      const isAnyRejected = item.order_details?.some(d => d.item.status === "rejected");
      return {
        id: item.id,
        order_number: item.order_number,
        approved_items_count: Number(item.approved_items_count) || 0,
        tracking_number: resolveTrackingNumber(item),
        carrier: resolveCarrier(item),
        tracking_url: resolveTrackingUrl(item),
        items: item.order_details?.map((d) => d.itemId),
        activeStatus: orderStatus,
        payment_status: item.payment_status,
        status: (
          <>
            <span
              style={{
                backgroundColor: getOrderDisplayStatusBackground(item),
                fontWeight: "bold",
                display: "inline-block",
                color: "white",
                padding: "4px 8px",
                borderRadius: "20px",
                minWidth: "90px",
                textAlign: "center",
              }}
            >
              {formatOrderDisplayStatusLabel(item)}
            </span>
            {(isAllApproved && orderStatus === "pending") && (
              <Tooltip title="All order items are approved — you can approve this order" arrow>
                <IconButton onClick={(e) => e.stopPropagation()} size="small">
                  <InfoIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {(isAnyRejected) && (
              <Tooltip title="You are a rejected a order or order item" arrow>
                <IconButton onClick={(e) => e.stopPropagation()} size="small">
                  <InfoIcon color="danger" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      }
    }) || [];

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const tableEl = (
    <OrderTable
      columns={columns}
      rows={allRecords}
      recordList={orderListFunc}
      orderDetailBasePath="/dashboard/orders"
    />
  );

  const titleEl = (
    <StyledTypographyText>
      {embedded ? "ORDERS" : "ORDER MANAGEMENT"}
    </StyledTypographyText>
  );

  const searchEl = (
    <StyledBoxStyle>
      <StyledCustomerSearch
        placeholder="Search by order id, customer name, locker assignments"
        variant="outlined"
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <StyledSearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <StyledStatusFilter
        value={statusFilter}
        onChange={handleStatusFilterChange}
        displayEmpty
        inputProps={{ "aria-label": "Filter by status" }}
      >
        {ORDER_STATUS_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </StyledStatusFilter>
    </StyledBoxStyle>
  );

  const content = (
    <StyledTableLayout $embedded={embedded}>
      {embedded ? (
        <StyledEmbeddedTableChrome>{titleEl}</StyledEmbeddedTableChrome>
      ) : (
        titleEl
      )}

      {embedded ? (
        <StyledEmbeddedTableChrome>{searchEl}</StyledEmbeddedTableChrome>
      ) : (
        searchEl
      )}

      <StyledOrdersContainer $embedded={embedded}>
        {showTableLoader ? (
          <Box sx={{ py: 2, ...(embedded && { flex: 1, minHeight: "320px" }) }}>
            <Loader minHeight={300} size={48} />
          </Box>
        ) : (
          <>
            {embedded ? (
              <StyledEmbeddedTableScroll>{tableEl}</StyledEmbeddedTableScroll>
            ) : (
              tableEl
            )}

            {!embedded && (
              <TablePagination
                component="div"
                count={total || allRecords.length}
                page={page - 1}
                onPageChange={(event, newPage) => setPage(newPage + 1)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            )}
          </>
        )}
      </StyledOrdersContainer>
    </StyledTableLayout>
  );

  if (embedded) {
    return content;
  }

  return <StyledWrapper>{content}</StyledWrapper>;
};

export default OrderComponent;
